/* eslint-disable react/prop-types */
import * as React from 'react';

import DocViewerPrimitive from '@cyntler/react-doc-viewer';
import { DocViewerRenderers, VideoRenderer } from '@cyntler/react-doc-viewer';
import type { DocRenderer } from '@cyntler/react-doc-viewer';
import '@cyntler/react-doc-viewer/dist/index.css';
import { Button, Typography } from 'antd';

import {
  CloseCircleOutlinedIcon,
  LeftOutlinedIcon,
  LoadingOutlinedIcon,
  RightOutlinedIcon,
} from 'components';
import { cn } from 'utils';

// Type definition for file loader function
type FileLoader = (props: {
  documentURI?: string;
  signal?: AbortSignal;
  fileLoaderComplete: (fileReader?: FileReader) => void;
}) => void;

// Cache for storing downloaded documents (using FileReader objects)
const documentCache = new Map<string, FileReader>();

// Create a caching wrapper for file loaders
const createCachedFileLoader = (originalLoader?: FileLoader): FileLoader => {
  return ({ documentURI, signal, fileLoaderComplete }) => {
    // Check if document is already cached
    if (documentCache.has(documentURI || '')) {
      const cachedData = documentCache.get(documentURI || '');
      fileLoaderComplete(cachedData);
      return;
    }

    // If no original loader, just complete without loading
    if (!originalLoader) {
      fileLoaderComplete();
      return;
    }

    // Use the original loader and cache the result
    originalLoader({
      documentURI,
      signal,
      fileLoaderComplete: (fileReader?: FileReader) => {
        if (fileReader && documentURI) {
          documentCache.set(documentURI, fileReader);
        }
        fileLoaderComplete(fileReader);
      },
    });
  };
};

// Custom video renderer with preload='metadata' attribute
const CustomVideoRenderer: DocRenderer = ({ mainState }) => {
  const { currentDocument } = mainState ?? {};

  if (!currentDocument) return null;

  // Use uri directly if fileData is not available (our custom loader)
  // Otherwise use fileData (for blob/data URLs)
  const videoSrc = currentDocument.fileData || currentDocument.uri;

  return (
    <div id='video-renderer'>
      <video
        controls
        src={videoSrc as string}
        preload='metadata'
        className='max-h-full w-full'
      >
        Your browser does not support the video tag.
      </video>
    </div>
  );
};
CustomVideoRenderer.fileTypes = VideoRenderer.fileTypes;
CustomVideoRenderer.weight = VideoRenderer.weight;
// Custom file loader that doesn't download the video
// Instead, it passes the URI directly to let the browser handle loading
CustomVideoRenderer.fileLoader = ({ fileLoaderComplete }) => {
  fileLoaderComplete();
};

const DocViewer = React.forwardRef<
  React.ElementRef<typeof DocViewerPrimitive>,
  React.ComponentPropsWithoutRef<typeof DocViewerPrimitive>
>(
  (
    {
      language = 'en',
      prefetchMethod = 'GET',
      pluginRenderers,
      documents,
      config,
      className,
      ...props
    },
    ref,
  ) => {
    const {
      header,
      loadingRenderer,
      noRenderer,
      pdfVerticalScrollByDefault = true,
      ...otherConfig
    } = config ?? {};

    // Replace default VideoRenderer with CustomVideoRenderer and wrap all file loaders with cache
    const customPluginRenderers = React.useMemo(() => {
      const renderers = pluginRenderers ?? DocViewerRenderers;
      return renderers.map(renderer => {
        // Use CustomVideoRenderer for videos
        if (renderer === VideoRenderer) {
          return CustomVideoRenderer;
        }

        // For other renderers, wrap their file loaders with caching only if they have one
        const CachedRenderer: DocRenderer = props => renderer(props);
        CachedRenderer.fileTypes = renderer.fileTypes;
        CachedRenderer.weight = renderer.weight;

        // Only wrap the file loader if the renderer has one
        if (renderer.fileLoader) {
          CachedRenderer.fileLoader = createCachedFileLoader(
            renderer.fileLoader,
          );
        }

        return CachedRenderer;
      });
    }, [pluginRenderers]);

    // Memoize documents to prevent re-fetching
    const memoizedDocuments = React.useMemo(() => documents, [documents]);

    return (
      <DocViewerPrimitive
        ref={ref}
        language={language}
        prefetchMethod={prefetchMethod}
        pluginRenderers={customPluginRenderers}
        documents={memoizedDocuments}
        config={{
          header: {
            overrideComponent: (
              { documents, currentDocument, currentFileNo, config },
              previousDocument,
              nextDocument,
            ) => {
              const { header: configHeader } = config ?? {};
              const { disableFileName } = configHeader ?? {};
              const documentsSize = documents.length;
              const isMultipleDocuments = documentsSize > 1;

              if (!currentDocument || disableFileName) {
                return null;
              }

              if (isMultipleDocuments) {
                return (
                  <div className='sticky inset-x-0 top-0 z-50 flex flex-wrap items-center justify-between gap-2 border-b bg-[--ant-color-bg-layout] p-4'>
                    <div className='flex flex-wrap items-center gap-1 text-sm'>
                      <span>فایل</span>
                      <span>{currentFileNo + 1}</span>
                      <span>از</span>
                      <span>{documentsSize}</span>
                    </div>
                    <div className='flex flex-wrap items-center gap-2'>
                      <Button
                        type='text'
                        size='small'
                        shape='circle'
                        icon={<RightOutlinedIcon />}
                        disabled={currentFileNo === 0}
                        onClick={previousDocument}
                      />
                      <Button
                        type='text'
                        size='small'
                        shape='circle'
                        icon={<LeftOutlinedIcon />}
                        disabled={currentFileNo >= documentsSize - 1}
                        onClick={nextDocument}
                      />
                    </div>
                  </div>
                );
              }

              return <React.Fragment />;
            },
            ...header,
          },
          loadingRenderer: {
            overrideComponent: ({ fileName }) => (
              <div className='grid content-center justify-items-center gap-4'>
                <LoadingOutlinedIcon className='text-2xl text-[--ant-color-text-description]' />
                <Typography.Paragraph className='mx-4 line-clamp-1'>
                  {fileName ? (
                    <React.Fragment>
                      <Typography.Text type='secondary'>
                        در حال بررسی فایل:{' '}
                      </Typography.Text>
                      <Typography.Text strong type='secondary'>
                        {fileName}
                      </Typography.Text>
                    </React.Fragment>
                  ) : (
                    <Typography.Text type='secondary'>
                      در حال بارگذاری...
                    </Typography.Text>
                  )}
                </Typography.Paragraph>
              </div>
            ),
            ...loadingRenderer,
          },
          noRenderer: {
            overrideComponent: ({ fileName }) => (
              <div className='grid content-center justify-items-center gap-4'>
                <CloseCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                <Typography.Paragraph className='mx-4 line-clamp-1'>
                  {fileName ? (
                    <React.Fragment>
                      <Typography.Text type='danger'>
                        خطا در بارگذاری فایل:{' '}
                      </Typography.Text>
                      <Typography.Text strong type='danger'>
                        {fileName}
                      </Typography.Text>
                    </React.Fragment>
                  ) : (
                    <Typography.Text type='danger'>
                      خطایی پیش آمده است.
                    </Typography.Text>
                  )}
                </Typography.Paragraph>
              </div>
            ),
            ...noRenderer,
          },
          pdfVerticalScrollByDefault,
          ...otherConfig,
        }}
        className={cn(
          'h-48 border [&_#image-renderer]:[background:inherit] [&_#pdf-controls]:hidden [&_:is(#proxy-renderer,#msdoc-renderer)]:flex-1 [&_:is(#proxy-renderer,#msdoc-renderer)]:overflow-auto *:[&_:is(#proxy-renderer,#msdoc-renderer)]:h-full',
          className,
        )}
        {...props}
      />
    );
  },
);
DocViewer.displayName = 'DocViewer';

export { DocViewer };
